import React, { useRef, useState, useCallback } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Button, Input, Space, Table, Tooltip } from "antd";
import Highlighter from "react-highlight-words";


const debounce = (func, delay) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

const TableComponent = ({
  columns,
  rows = [],
  loading,
  scrollx,
  scrolly,
  totalPages,
  onChange,
  locale,
}) => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);

  const debouncedSearch = useCallback(
    debounce((selectedKeys, confirm, dataIndex) => {
      confirm();
      onChange({
        current: 1,
        filterField: dataIndex,
        search: selectedKeys[0],
      });
    }, 700),
    [onChange]
  );

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
    debouncedSearch(selectedKeys, confirm, dataIndex);
  };

  const handleReset = (clearFilters, confirm, dataIndex) => {
    clearFilters();
    setSearchText("");
    setSearchedColumn("");
    onChange({
      current: 1,
      filterField: dataIndex,
      search: "",
    });
    confirm();
  };

  const getColumnSearchProps = (dataIndex, title) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${title}`}
          value={selectedKeys[0]}
          onChange={(e) => {
            setSelectedKeys(e.target.value ? [e.target.value] : []);
            if (e.target.value.length >= 3) {
              debouncedSearch(selectedKeys, confirm, dataIndex);
            }
          }}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            // type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() =>
            clearFilters && handleReset(clearFilters, confirm, dataIndex)
            }
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
          <Button
            // type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
    onFilter: dataIndex === "availableAfter" ? undefined : (value, record) => {
      if (dataIndex === "experience") {
        return parseFloat(record[dataIndex]) >= parseFloat(value);
      }
      if (dataIndex === "available") {
        return record[dataIndex] ? record[dataIndex] == (value.toLowerCase() == "yes") : "No";
      }
      if (dataIndex === "atClient") {
        return record[dataIndex] ? record[dataIndex] == (value.toLowerCase() == "yes") : "No";
      }
      if(dataIndex === "projects") {
        return record.projectDetails && record.projectDetails.length > 0
      }
      return record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase());
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) => {
      return searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      );
    },
  });

  const newCol = columns.map((element) => {
    if (element.dataIndex === "employeeNo" && element.change) {
      return {
        ...element,
        // Remove sorter property to disable sorting
        sorter: undefined,
        sortDirections: [], // You can omit this if sorter is disabled
        ...getColumnSearchProps(element.dataIndex, element.title), // Keep filtering functionality
      };
    }
    if (element.dataIndex === "skills" && element.change) {
      return {
        ...element,
        sorter: undefined,
        sortDirections: [],
        ...getColumnSearchProps(element.dataIndex, element.title),
        render: (text, record) => {
          const skills = record.skills && record.skills.length > 0 ? record.skills.join(", ") : "";
          const truncatedSkills = skills.slice(0, 30) + (skills.length > 30 ? "..." : "");
          return (
            <Tooltip title={skills}>
              <span>{truncatedSkills}</span>
            </Tooltip>
          );
        }
      };
    }
    if (element.dataIndex === "projects" && element.change) {
      return {
        ...element,
        sorter: undefined,
        sortDirections: [], 
        ...getColumnSearchProps(element.dataIndex, element.title),
        render: (text, record) => {
          const projects = record.projectDetails && record.projectDetails.length > 0
            ? record.projectDetails
                .map((project) => project.projectName)
                .join(", ")
            : "";
          const truncatedProjects = projects.slice(0, 30) + (projects.length > 30 ? "..." : "");
          return (
            <Tooltip title={projects}>
              <span>{truncatedProjects}</span>
            </Tooltip>
          );
        }
      };
    }
    if (element.dataIndex === "remarks" && element.change) {
      return {
        ...element,
        sorter: undefined,
        sortDirections: [],
        render: (text) => {
          const truncatedText = text ? text.slice(0, 70) + (text.length > 70 ? "..." : "") : "";
          return (
            <Tooltip title={text}>
              <span>{truncatedText}</span>
            </Tooltip>
          );
        },
      };
    }
    if (element.change) {
      return {
        ...element,
        sorter: (a, b) => {
          const valA = a[element.dataIndex];
          const valB = b[element.dataIndex];

          if (valA < valB) {
            return -1;
          }
          if (valA > valB) {
            return 1;
          }
          return 0;
        },
        sortDirections: ["ascend", "descend", "ascend"],
        ...getColumnSearchProps(element.dataIndex, element.title),
        render: (text, record) => {
          if (element.dataIndex == "available") {
            return text ? "Yes" : "No";
          }
          if (element.dataIndex === "managerName") {
            return record.managerName
              ? record.managerName
              : "";
          }
          if (element.dataIndex == "atClient") {
            return text ? "Yes" : "No";
          }
          if (element.dataIndex == "preferredLocation") {
            const truncatedPreferredLocation = text
              ? text.slice(0, 30) + (text.length > 30 ? "..." : "")
              : "";
            return (
              <Tooltip title={text}>
                <span>{truncatedPreferredLocation}</span>
              </Tooltip>
            );
          }
          return text;
        },
      };
    }
    return element;
  });

  return (
    <Table
      columns={newCol}
      dataSource={
        rows.length && rows?.map((row, index) => ({ ...row, key: index }))
      }
      loading={loading}
      scroll={{ x: scrollx, y: scrolly }}
      pagination={{
        total: totalPages,
        pageSize: 50,
        current: currentPage,
        showSizeChanger: false,
        onChange: (page) => {
          setCurrentPage(page);
        },
      }}
      rowClassName={(record, index) =>
        index % 2 === 0 ? "even-row" : ""
      }
      components={{
        header: {
          cell: (props) => (
            <th
              {...props}
              style={{
                ...props.style,
                padding: "8px 10px",
                backgroundColor: "#d5d5d5",
                position: props.style?.position || "static",
              }}
            />
          ),
        },
        body: {
          cell: (props) => (
            <td
              {...props}
              style={{
                ...props.style,
                padding: "1px 12px",
                position: props.style?.position || "static",
              }}
            />
          ),
        },
      }}
      onChange={(pagination = { current: 1 }, filters, sorter) => {
        const isSortOperation = sorter.field && sorter.order;
        const filterField = !isSortOperation && Object.keys(filters || {}).length
          ? Object.keys(filters)[0]
          : null;
        const search =
          filterField && filters[filterField] ? filters[filterField][0] : "";
        const sortField = sorter.field || null;
        const sortOrder = sorter.order || null;

        if (sortOrder === "ascend" || sortOrder === "descend") {
          setCurrentPage(pagination.current || 1);
          onChange({
            pagination: { current: pagination.current || 1 },
            filterField,
            search,
            sortField,
            sortOrder,
          });
        } else {
          const isSortOperation = sorter.field && sorter.order;
          const filterField = !isSortOperation && Object.keys(filters || {}).length
            ? Object.keys(filters)[0]
            : null;
          const search =
            filterField && filters[filterField] ? filters[filterField][0] : "";
          onChange({
            pagination: { current: pagination.current || 1 },
            filterField,
            search,
            sortField: null,
            sortOrder: null,
          });
        }
      }}
      locale={locale}
      bordered
    />
  );
};

export default TableComponent;
